import { useContext } from 'react';
import { graphql } from 'react-relay';
import { usePageSpaceKey } from '@confluence/page-context';
import { expValEquals } from '@confluence/feature-experiments';

import { SpaceDetailContext } from './SpaceDetailContext.ts';

import type {
	useSpaceHomepageIdSpaceHomepageIdQuery as useSpaceHomepageIdSpaceHomepageIdQuery$data,
	useSpaceHomepageIdSpaceHomepageIdQueryVariables as useSpaceHomepageIdSpaceHomepageIdQuery$variables,
} from './__types__/useSpaceHomepageIdSpaceHomepageIdQuery.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

export const useSpaceHomepageId = (spaceKey?: string): string | undefined => {
	const [pageContextSpaceKey] = usePageSpaceKey();
	spaceKey = spaceKey ?? pageContextSpaceKey;
	const sharedDetail = useContext(SpaceDetailContext);
	const canUseSharedDetail =
		Boolean(spaceKey) &&
		sharedDetail?.spaceKey === spaceKey &&
		expValEquals('confluence_editor_content_unified_query', 'isEnabled', true);
	const { data } = useQueryInterop<
		useSpaceHomepageIdSpaceHomepageIdQuery$data,
		useSpaceHomepageIdSpaceHomepageIdQuery$variables
	>(
		graphql`
			query useSpaceHomepageIdSpaceHomepageIdQuery($spaceKey: String) {
				space(key: $spaceKey) {
					homepage {
						id
					}
					id
				}
			}
		`,
		{
			variables: { spaceKey },
			skip: !spaceKey || canUseSharedDetail,
		},
	);
	if (canUseSharedDetail) {
		return sharedDetail?.space?.homepage?.id || undefined;
	}
	return data?.space?.homepage?.id || undefined;
};
